import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/ui/Button";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors so a single bad record cannot turn the whole app
 * into a white screen. The message is intentionally generic; details go to
 * the console for the developer.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[render error]", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex h-full items-center justify-center bg-shell px-4">
        <div className="w-full max-w-sm rounded-xl border border-line bg-paper p-6 text-center shadow-card">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-dangerSoft text-danger">
            <AlertTriangle size={18} />
          </div>
          <h1 className="mt-3 text-[15px] font-semibold">
            Có gì đó không ổn
          </h1>
          <p className="mt-1 text-[13px] leading-6 text-ink-faint">
            Giao diện gặp lỗi không mong muốn. Dữ liệu của bạn vẫn an toàn trên
            server.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button
              variant="primary"
              onClick={() => this.setState({ error: null })}
            >
              <RotateCcw size={14} />
              Thử lại
            </Button>
            <Button onClick={() => window.location.reload()}>Tải lại trang</Button>
          </div>
          {import.meta.env.DEV && (
            <pre className="mt-4 max-h-32 overflow-auto rounded-lg bg-shell p-2 text-left font-mono text-[11px] text-ink-faint">
              {this.state.error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
